// Device + camera facts recorded with every scan and uploaded in
// `capture-meta.json`'s `device` block.
//
// Why this rides along on real customer scans and not just diag captures: the
// capture problems we chase are device-shaped. The iPhone zoom-crop that eats
// the heel, a phone whose rear track never gives more than 720p, a GPU the
// guidance model can't run on — none of that is visible in the photos alone,
// and by the time a scan looks wrong the phone is long gone. The `diag` block
// covers the same ground but only exists when a tester turned diag mode on, so
// it never covers the scans that actually went sideways.
//
// What is NOT readable, so nobody goes looking for it:
//   - The exact iPhone model. Every iPhone reports "iPhone" and Safari ships no
//     UA client hints, so `model` stays null there. `screen` + `camera.label`
//     ("Back Dual Wide Camera" vs "Back Triple Camera") narrow it to a class,
//     which is what the fields are for; they are not a model lookup.
//   - `memoryGB` and `model` on any non-Chromium browser.
//
// Every read is best-effort and individually guarded: this runs on the capture
// critical path, and a missing field must never cost a customer their scan.

/** Portrait-normalised screen geometry, so a landscape read still compares. */
export interface DeviceScreen {
  /** Short edge in CSS px. */
  width: number;
  /** Long edge in CSS px. */
  height: number;
  /** `devicePixelRatio` — with `width`/`height` this is the model-class datum. */
  dpr: number;
}

/** Active rear-lens facts, read off the live track before it is stopped. */
export interface DeviceCamera {
  /** Lens label, e.g. "Back Dual Wide Camera" — localised by the platform. */
  label: string;
  facingMode: string | null;
  /** Settings actually in effect for the capture stream. */
  width: number | null;
  height: number | null;
  frameRate: number | null;
  /** Ceiling the track reports — what the stream could have run at. */
  maxWidth: number | null;
  maxHeight: number | null;
  /** Zoom range, when the platform exposes it (the heel-crop suspect). */
  zoomMin: number | null;
  zoomMax: number | null;
}

export interface DeviceInfo {
  ua: string;
  /** UA-CH model (Chromium/Android), e.g. "SM-S911B". Null on Safari/iOS. */
  model: string | null;
  platform: string | null;
  platformVersion: string | null;
  screen: DeviceScreen | null;
  /** `hardwareConcurrency` — logical cores. */
  cores: number | null;
  /** `deviceMemory` in GB (Chromium only, coarse by design). */
  memoryGB: number | null;
  /** Unmasked WebGL renderer, e.g. "Adreno (TM) 740". "Apple GPU" on iOS. */
  gpu: string | null;
  camera: DeviceCamera | null;
}

interface UaDataValues {
  model?: string;
  platform?: string;
  platformVersion?: string;
}

interface NavigatorUaData {
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<UaDataValues>;
}

/** Finite-number guard: `getSettings()` happily returns undefined or NaN. */
function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/**
 * UA client hints. Chromium-only and async (the browser may prompt-gate high
 * entropy hints), which is why the whole collector is async. On Android this is
 * the one place the real model string still appears — Chrome's UA reduction
 * replaced it with "K" in the classic UA string.
 */
async function readUaHints(): Promise<Pick<DeviceInfo, "model" | "platform" | "platformVersion">> {
  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUaData }).userAgentData;
  const platform = uaData?.platform ?? null;
  if (!uaData?.getHighEntropyValues) {
    return { model: null, platform, platformVersion: null };
  }
  try {
    const v = await uaData.getHighEntropyValues(["model", "platformVersion"]);
    return {
      model: v.model || null,
      platform: v.platform || platform,
      platformVersion: v.platformVersion || null,
    };
  } catch {
    return { model: null, platform, platformVersion: null };
  }
}

/** Portrait-normalised, so the same phone reads the same in either rotation. */
function readScreen(): DeviceScreen | null {
  const w = num(globalThis.screen?.width);
  const h = num(globalThis.screen?.height);
  const dpr = num(globalThis.devicePixelRatio) ?? 1;
  if (w === null || h === null) return null;
  return { width: Math.min(w, h), height: Math.max(w, h), dpr };
}

/**
 * Unmasked WebGL renderer via WEBGL_debug_renderer_info. Uses a throwaway 1×1
 * context and drops it again immediately — a leaked context costs a real GPU
 * slot on mobile, and the guidance session needs those.
 */
function readGpu(): string | null {
  try {
    const canvas = document.createElement("canvas");
    const gl = (canvas.getContext("webgl") ??
      canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return null;
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    const renderer = ext
      ? (gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string | null)
      : (gl.getParameter(gl.RENDERER) as string | null);
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return renderer || null;
  } catch {
    return null;
  }
}

/**
 * Active-track facts. Must be called while the stream is live: `getSettings()`
 * and `getCapabilities()` are empty once the track is stopped, and the capture
 * flow stops it before the upload runs.
 */
function readCamera(stream: MediaStream | null | undefined): DeviceCamera | null {
  const track = stream?.getVideoTracks?.()[0];
  if (!track) return null;
  let settings: MediaTrackSettings = {};
  try {
    settings = track.getSettings?.() ?? {};
  } catch {
    /* platform refuses settings — capabilities below may still answer */
  }
  let caps: MediaTrackCapabilities & { zoom?: { min?: number; max?: number } } = {};
  try {
    caps = track.getCapabilities?.() ?? {};
  } catch {
    /* not implemented on this platform (Firefox, older Safari) */
  }
  return {
    label: track.label ?? "",
    facingMode: (settings.facingMode as string | undefined) ?? null,
    width: num(settings.width),
    height: num(settings.height),
    frameRate: num(settings.frameRate),
    maxWidth: num(caps.width?.max),
    maxHeight: num(caps.height?.max),
    zoomMin: num(caps.zoom?.min),
    zoomMax: num(caps.zoom?.max),
  };
}

/**
 * Collect everything the browser will tell us about this phone and its rear
 * lens. Never throws and never rejects: on a platform that answers nothing, the
 * block is still valid with `ua` set and the rest null.
 */
export async function collectDeviceInfo(
  stream: MediaStream | null | undefined,
): Promise<DeviceInfo> {
  const hints = await readUaHints();
  return {
    ua: navigator.userAgent,
    ...hints,
    screen: readScreen(),
    cores: num((navigator as Navigator & { hardwareConcurrency?: number }).hardwareConcurrency),
    memoryGB: num((navigator as Navigator & { deviceMemory?: number }).deviceMemory),
    gpu: readGpu(),
    camera: readCamera(stream),
  };
}
